import {
    FC,
    ReactElement,
    useEffect,
    useMemo,
} from 'react';

import { URLSearchParamsInit, useSearchParams } from 'react-router-dom';

import { BlogOverview } from '../../../containers';
import { QuerySortDirection } from '../../../entities/@api/Strapi';
import { BlogOverviewBlockInterface } from '../../../entities/@blocks/BlogOverview/BlogOverview';
import { paginationPageParameter } from '../../../entities/Pagination/Pagination';
import { generateStoreKey } from '../../../helpers/store';
import { fetchBlogOverview } from '../../../redux/@blog/blogOverviews/blogOverviewsActions';
import { setHasServerData } from '../../../redux/app/appSlice';
import { useTypedDispatch, useTypedSelector } from '../../../redux/store';

interface ConnectedBlogOverviewProps extends BlogOverviewBlockInterface {
    className?: string;
}

export const ConnectedBlogOverview: FC<ConnectedBlogOverviewProps> = ({
    categories,
    amount = 13,
    className = '',
    ...blogOverviewProps
}): ReactElement => {
    const [, setSearchParams] = useSearchParams();

    const dispatch = useTypedDispatch();

    const hasServerData = useTypedSelector(state => state.appSlice.hasServerData);

    const isLoading = useTypedSelector(state => state.blogOverviewsSlice.isLoading);
    const blogOverviews = useTypedSelector(state => state.blogOverviewsSlice.overviews);

    const categoryString = useMemo((): string => categories.join(','), [categories]);
    const key = generateStoreKey(categoryString.split(','));

    useEffect((): void => {
        if (hasServerData) {
            dispatch(setHasServerData(false));

            if (blogOverviews[key]) {
                return;
            }
        }

        setSearchParams({}, { replace: true });

        dispatch(fetchBlogOverview({
            key,
            categories: categoryString.split(','),
            sort: {
                date: QuerySortDirection.desc,
            },
            pagination: {
                pageSize: amount,
                page: 1,
            },
        }));
    }, [categoryString]);

    const handlePaginationChange = (page: number): void => {
        const paginationSearchParams: URLSearchParamsInit = page !== 1
            ? { [paginationPageParameter]: page.toString() }
            : {};

        setSearchParams({
            ...paginationSearchParams,
        }, {
            replace: true,
        });

        dispatch(fetchBlogOverview({
            key,
            categories: categoryString.split(','),
            sort: {
                date: QuerySortDirection.desc,
            },
            pagination: {
                pageSize: amount,
                page,
            },
        }));
    };

    return (
        <BlogOverview
            {...blogOverviewProps}
            isLoading={isLoading}
            blogPosts={blogOverviews[key]?.blogPosts || []}
            pagination={blogOverviews[key]?.pagination}
            onPaginationChange={handlePaginationChange}
            className={className}
        />
    );
};
